import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getCase, syncCase } from "../lib/cityPortal";
import { ChevronRight, RefreshCw } from "lucide-react";
import { relativeDate, StagePill } from "../lib/ownerUi";
import BackBar from "../components/BackBar";

const SYNC_TONE = {
  synced: { fg: "#4F6149", bg: "rgba(79,97,73,0.10)", label: "Synced" },
  pending: { fg: "#8a651f", bg: "rgba(185,138,60,0.14)", label: "Pending sync" },
  error: { fg: "#A23B2E", bg: "rgba(162,59,46,0.10)", label: "Sync error" },
};

export default function CityCaseDetail() {
  const { caseId } = useParams();
  const { user } = useRole();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    try {
      setC(await getCase(caseId, user.id));
    } catch (e) {
      setErr(e);
    }
  };
  useEffect(() => {
    if (!user?.id) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, caseId]);

  if (err) {
    return <p style={{ color: "#A23B2E" }} data-testid="city-case-error">Could not load this case.</p>;
  }
  if (!c) {
    return <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="city-case-loading">Loading case…</div>;
  }

  const tone = SYNC_TONE[c.sync_status] || SYNC_TONE.synced;

  const onSync = async () => {
    setSyncing(true);
    try {
      const fresh = await syncCase(caseId, user.id);
      setC(fresh);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="city-case-detail">
      <BackBar
        parent={{ to: "/city/cases", label: "Cases" }}
        trail={[
          { label: "City", to: "/city" },
          { label: "Cases", to: "/city/cases" },
          { label: c.case_id },
        ]}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div
            className="tabular-nums"
            style={{ color: "#3A5563", fontWeight: 700, fontSize: 18 }}
            data-testid="case-id"
          >
            {c.case_id}
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 30px)",
              fontWeight: 600,
              color: "#1B1F4B",
              marginTop: 4,
            }}
          >
            {c.parcel?.address || "—"}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
            }}
          >
            {c.parcel?.neighborhood || "—"} · {c.design?.name || "—"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StagePill stage={c.current_stage} />
          <span
            style={{
              padding: "3px 10px",
              borderRadius: 999,
              background: tone.bg,
              color: tone.fg,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
            data-testid="case-sync-status"
          >
            {tone.label}
          </span>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12,
            }}
          >
            Last sync {relativeDate(c.last_sync_at)}
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <div className="mt-6 grid lg:grid-cols-[2fr_1fr] gap-7">
        <div className="wn-card" style={{ padding: 18 }} data-testid="case-timeline">
          <div className="flex items-center justify-between">
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Permitting events.
            </h2>
            <button
              className="wn-btn wn-btn-tertiary"
              onClick={onSync}
              disabled={syncing}
              data-testid="case-sync-now"
            >
              <RefreshCw size={14} /> {syncing ? "Syncing…" : "Sync now"}
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {(c.timeline || []).map((e, i) => (
              <div key={i} className="flex items-start gap-3">
                <span
                  aria-hidden
                  style={{
                    marginTop: 6,
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "#B14E2F",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={{ fontSize: 13, color: "#272636" }}>{e.label}</div>
                  <div
                    style={{
                      fontFamily: '"Newsreader", Georgia, serif',
                      fontStyle: "italic",
                      color: "#6C6B85",
                      fontSize: 11.5,
                    }}
                  >
                    {relativeDate(e.at)}
                  </div>
                </div>
              </div>
            ))}
            {(c.timeline || []).length === 0 && (
              <div style={{ color: "#6C6B85", fontSize: 13 }}>No permitting events recorded.</div>
            )}
          </div>
        </div>

        <aside className="space-y-5" data-testid="case-side">
          <div className="wn-card" style={{ padding: 16 }}>
            <div className="wn-label">Design</div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 16,
                color: "#1B1F4B",
                marginTop: 4,
              }}
            >
              {c.design?.name || "—"}
            </div>
            <Link
              to={`/city/reviews/${c.design?.id}`}
              className="wn-btn wn-btn-tertiary mt-2"
              style={{ paddingLeft: 0 }}
            >
              View design <ChevronRight size={14} />
            </Link>
          </div>

          <div className="wn-card" style={{ padding: 16 }}>
            <div className="wn-label">Architect of record</div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 15,
                color: "#1B1F4B",
                marginTop: 4,
              }}
            >
              {c.architect?.firm || "—"}
            </div>
            {c.architect?.id && (
              <Link
                to={`/architects/${c.architect.id}`}
                className="wn-btn wn-btn-tertiary mt-2"
                style={{ paddingLeft: 0 }}
              >
                Open profile <ChevronRight size={14} />
              </Link>
            )}
          </div>

          <div className="wn-card" style={{ padding: 16 }}>
            <div className="wn-label">Owner</div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 15,
                color: "#1B1F4B",
                marginTop: 4,
              }}
            >
              {c.owner?.organization || c.owner?.name || "—"}
            </div>
          </div>

          <Link
            to={`/city/projects/${c.project_id}`}
            className="wn-btn wn-btn-primary w-full"
            data-testid="case-view-project"
          >
            View project record <ChevronRight size={16} />
          </Link>
        </aside>
      </div>
    </section>
  );
}
